'use client';

import { useEffect, useMemo, useState } from "react";
import { useModuleRuntime } from "@flowx/module-runtime/client";
import AgentCard, { defaultAgents } from "./components/AgentCard";
import ChatInput from "./components/ChatInput";
import WorkflowCard from "./components/WorkflowCard";
import ChatSidebar, { mockHistories } from "./components/ChatSidebar";
import MessageBubble from "./components/MessageBubble";
import { createXiaowanApi, type Agent, type Conversation, type Message as ApiMessage } from "./api";
import {
  loadXiaowanConversation,
  loadXiaowanInitialData,
  requestXiaowanChat,
} from "./safe-fallbacks";

// 分类Tab定义
const categoryTabs = [
  { id: "explore", name: "探索", icon: "🔍" },
  { id: "discover", name: "发现", icon: "✨" },
  { id: "inspiration", name: "灵感", icon: "💡" },
  { id: "data", name: "数据", icon: "📊" },
  { id: "creation", name: "创作", icon: "🎨" },
  { id: "efficiency", name: "效率", icon: "⚡" },
];

// 按分类组织的智能体/工作流数据
const categoryWorkflows: Record<string, Array<{
  title: string;
  description: string;
  thumbnail: string;
  code?: string;
}>> = {
  explore: [
    { title: "AI助手", description: "通用AI对话助手，回答各类问题，提供智能建议", thumbnail: "https://picsum.photos/seed/ai-assistant/144/256", code: "assistant" },
    { title: "知识问答", description: "基于知识库的智能问答，快速获取专业信息", thumbnail: "https://picsum.photos/seed/knowledge/144/256", code: "knowledge" },
    { title: "翻译助手", description: "多语言翻译，支持中英日韩等50+语言", thumbnail: "https://picsum.photos/seed/translate/144/256", code: "translate" },
    { title: "代码助手", description: "编程辅助，代码生成、调试、解释", thumbnail: "https://picsum.photos/seed/code/144/256", code: "code" },
    { title: "学习导师", description: "个性化学习指导，制定学习计划", thumbnail: "https://picsum.photos/seed/learn/144/256", code: "learn" },
    { title: "阅读伙伴", description: "文章总结、书籍解读、阅读理解", thumbnail: "https://picsum.photos/seed/read/144/256", code: "read" },
  ],
  discover: [
    { title: "热点追踪", description: "实时追踪行业热点，发现市场机会", thumbnail: "https://picsum.photos/seed/trending/144/256", code: "trending" },
    { title: "竞品分析", description: "深度分析竞品数据，制定差异化策略", thumbnail: "https://picsum.photos/seed/compete/144/256", code: "compete" },
    { title: "选品推荐", description: "基于市场数据的智能选品建议", thumbnail: "https://picsum.photos/seed/recommend/144/256", code: "recommend" },
    { title: "用户洞察", description: "分析用户行为，挖掘潜在需求", thumbnail: "https://picsum.photos/seed/insight/144/256", code: "insight" },
    { title: "趋势预测", description: "AI预测市场趋势，提前布局", thumbnail: "https://picsum.photos/seed/predict/144/256", code: "predict" },
    { title: "机会发现", description: "发现蓝海市场，寻找增长点", thumbnail: "https://picsum.photos/seed/opportunity/144/256", code: "opportunity" },
  ],
  inspiration: [
    { title: "创意生成", description: "激发创意灵感，生成独特创意方案", thumbnail: "https://picsum.photos/seed/creative/144/256", code: "creative" },
    { title: "文案大师", description: "AI文案创作，多种风格随心选", thumbnail: "https://picsum.photos/seed/copywrite/144/256", code: "copywrite" },
    { title: "脚本创作", description: "短视频脚本、广告文案一键生成", thumbnail: "https://picsum.photos/seed/script/144/256", code: "script" },
    { title: "故事生成", description: "AI故事创作，小说、剧本生成", thumbnail: "https://picsum.photos/seed/story/144/256", code: "story" },
    { title: "标题生成", description: "爆款标题生成，提升点击率", thumbnail: "https://picsum.photos/seed/title/144/256", code: "title" },
    { title: "灵感卡片", description: "随机生成灵感卡片，打破思维定式", thumbnail: "https://picsum.photos/seed/cards/144/256", code: "cards" },
  ],
  data: [
    { title: "数据分析", description: "智能分析销售数据，生成可视化报告", thumbnail: "https://picsum.photos/seed/analysis/144/256", code: "analysis" },
    { title: "经营会计", description: "财务数据分析，风险预警，经营建议", thumbnail: "https://picsum.photos/seed/accounting/144/256", code: "accounting" },
    { title: "报表生成", description: "自动生成各类数据报表", thumbnail: "https://picsum.photos/seed/report/144/256", code: "report" },
    { title: "库存预测", description: "AI预测销量趋势，优化库存周转", thumbnail: "https://picsum.photos/seed/inventory/144/256", code: "inventory" },
    { title: "价格优化", description: "智能定价建议，最大化利润", thumbnail: "https://picsum.photos/seed/pricing/144/256", code: "pricing" },
    { title: "ROI计算", description: "投资回报分析，优化资源配置", thumbnail: "https://picsum.photos/seed/roi/144/256", code: "roi" },
  ],
  creation: [
    { title: "模特工作流", description: "快速生成模特穿搭图片，支持多种风格", thumbnail: "https://picsum.photos/seed/model/144/256", code: "model" },
    { title: "产品拍摄", description: "AI智能商拍，一键生成专业产品图", thumbnail: "https://picsum.photos/seed/product/144/256", code: "product" },
    { title: "详情页生成", description: "一键生成电商详情页设计", thumbnail: "https://picsum.photos/seed/detail/144/256", code: "detail" },
    { title: "买家秀生成", description: "智能生成真实买家秀图片", thumbnail: "https://picsum.photos/seed/buyer/144/256", code: "buyer" },
    { title: "视频脚本", description: "AI生成短视频脚本和分镜", thumbnail: "https://picsum.photos/seed/video/144/256", code: "video" },
    { title: "海报设计", description: "智能海报生成，多模板可选", thumbnail: "https://picsum.photos/seed/poster/144/256", code: "poster" },
  ],
  efficiency: [
    { title: "客服助手", description: "AI客服自动回复，7x24小时在线", thumbnail: "https://picsum.photos/seed/service/144/256", code: "service" },
    { title: "邮件助手", description: "智能邮件撰写，提高沟通效率", thumbnail: "https://picsum.photos/seed/email/144/256", code: "email" },
    { title: "会议纪要", description: "自动生成会议纪要，提取关键信息", thumbnail: "https://picsum.photos/seed/meeting/144/256", code: "meeting" },
    { title: "日程管理", description: "智能日程规划，提高时间效率", thumbnail: "https://picsum.photos/seed/schedule/144/256", code: "schedule" },
    { title: "任务分解", description: "大任务智能拆解，生成执行计划", thumbnail: "https://picsum.photos/seed/task/144/256", code: "task" },
    { title: "人力资源", description: "招聘、考勤、员工管理助手", thumbnail: "https://picsum.photos/seed/hr/144/256", code: "hr" },
  ],
};

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  suggestions?: string[];
}

export function XiaowanPage() {
  const { api } = useModuleRuntime();
  const xiaowanApi = useMemo(() => createXiaowanApi(api), [api]);
  const [isInChat, setIsInChat] = useState(false);  // 是否有对话消息
  const [showSidebar, setShowSidebar] = useState(false);  // 是否显示侧边栏
  const [messages, setMessages] = useState<Message[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>(["写作"]);
  const [activeTab, setActiveTab] = useState("explore");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentAgentCode, setCurrentAgentCode] = useState<string>("xiaowan");
  const [agents, setAgents] = useState<Agent[]>([]);
  // const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch agents and workflows on mount
  useEffect(() => {
    const fetchData = async () => {
      const result = await loadXiaowanInitialData(xiaowanApi);
      if (!result) {
        return;
      }
      if (result.agentsResponse.data?.items) {
        setAgents(result.agentsResponse.data.items);
      }
      if (result.conversationsResponse.data?.items) {
        setConversations(result.conversationsResponse.data.items);
      }
    };

    fetchData();
  }, [xiaowanApi]);

  const handleSendMessage = async (content: string) => {
    // Add user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      isUser: true,
    };
    setMessages((prev) => [...prev, userMessage]);

    // Switch to chat mode
    if (!isInChat) {
      setIsInChat(true);
      setShowSidebar(true);
    }

    setIsLoading(true);

    try {
      const result = await requestXiaowanChat(xiaowanApi, {
        conversation_id: conversationId || undefined,
        agent_code: currentAgentCode,
        content,
      });

      if (result.kind === "response" && result.response.data) {
        const responseData = result.response.data;
        // Update conversation ID
        if (!conversationId) {
          setConversationId(responseData.conversation_id);
        }

        // Add AI response
        const aiMessage: Message = {
          id: responseData.message.id.toString(),
          content: responseData.message.content,
          isUser: false,
          suggestions: ["继续深入分析", "换个角度思考", "给我更多建议"],
        };
        setMessages((prev) => [...prev, aiMessage]);
      } else if (result.kind === "fallback") {
        setMessages((prev) => [...prev, {
          id: (Date.now() + 1).toString(),
          ...result.message,
          isUser: false,
        }]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAgentClick = (agentName: string, agentCode?: string) => {
    setSelectedTags([agentName]);
    setCurrentAgentCode(agentCode || "xiaowan");
    setIsInChat(true);
    setShowSidebar(true);
    setConversationId(null);
    // Add initial AI greeting
    const aiMessage: Message = {
      id: Date.now().toString(),
      content: `你好！我是${agentName}智能体，很高兴为你服务。请问有什么可以帮助你的？`,
      isUser: false,
      suggestions: ["开始培训", "查看历史记录", "了解功能介绍"],
    };
    setMessages([aiMessage]);
  };

  const handleNewChat = () => {
    setMessages([]);
    setSelectedTags(["写作"]);
    setConversationId(null);
    setCurrentAgentCode("xiaowan");
    // 回到首页内容，但保持侧边栏显示
    setIsInChat(false);
    setShowSidebar(true);
  };

  // const handleBackToHome = () => {
  //   setMessages([]);
  //   setIsInChat(false);
  //   setShowSidebar(false);
  //   setSelectedTags(["写作"]);
  //   setConversationId(null);
  // };

  const handleSelectChat = async (uuid: string) => {
    // 如果是当前新建的对话，不需要加载
    if (uuid === 'current') {
      return;
    }
    
    const result = await loadXiaowanConversation(xiaowanApi, uuid);
    if (!result) {
      return;
    }
    const { conversationResponse: convRes, messagesResponse: msgsRes } = result;

    if (convRes.data) {
      setConversationId(uuid);
      setCurrentAgentCode(convRes.data.conversation.agent_code);
      setSelectedTags([convRes.data.agent?.name || "对话"]);
    }

    if (msgsRes.data?.items) {
      const loadedMessages: Message[] = msgsRes.data.items.map((m: ApiMessage) => ({
        id: m.id.toString(),
        content: m.content,
        isUser: m.role === 'user',
      }));
      setMessages(loadedMessages);
    }

    setIsInChat(true);
    setShowSidebar(true);
  };

  // Display agents - use API data or fallback to defaults
  const displayAgents = agents.length > 0
    ? agents.slice(0, 4).map((a) => ({
        icon: a.avatar_url || "🤖",
        name: a.name,
        description: a.description,
        code: a.code,
      }))
    : defaultAgents;

  // Display workflows - 根据当前选中的分类Tab显示对应的工作流
  const currentCategoryWorkflows = categoryWorkflows[activeTab] || categoryWorkflows.explore;
  
  // 如果有搜索词，过滤工作流
  const displayWorkflows = searchQuery.trim()
    ? currentCategoryWorkflows.filter(
        w => w.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
             w.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : currentCategoryWorkflows;

  // Display conversation history
  // 如果有当前对话（通过agent开始的新对话），添加到列表最前面
  const currentChatItem = isInChat && selectedTags[0] && !conversationId 
    ? [{ id: 'current', title: selectedTags[0], type: 'text' as const, isActive: true }]
    : [];
    
  const baseHistories = conversations.length > 0
    ? conversations.map((c) => ({
        id: c.uuid,
        title: c.title || c.last_message?.slice(0, 20) || '新对话',
        type: 'text' as const,
      }))
    : mockHistories;
    
  const displayHistories = [...currentChatItem, ...baseHistories];

  // 有对话消息时显示对话界面
  if (isInChat && messages.length > 0) {
    // 计算侧边栏宽度用于标题栏偏移
    const sidebarWidth = isSidebarCollapsed ? 64 : 280;
    
    return (
      <div className="h-[calc(100vh-60px)] relative bg-bg-secondary">
        {/* Sidebar - 固定定位，不影响主内容布局 */}
        <div className="fixed left-0 top-15 h-[calc(100vh-60px)] z-20">
          <ChatSidebar
            histories={displayHistories}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            activeId={conversationId || (isInChat && !conversationId ? 'current' : undefined)}
            isCollapsed={isSidebarCollapsed}
          />
        </div>

        {/* Main Chat Area - 全屏宽度，内容居中 */}
        <div className="w-full h-full flex flex-col">
          {/* Header - 对话标题栏，需要偏移避开侧边栏 */}
          <div 
            className="h-14 px-6 flex items-center justify-between border-b border-white/8 sticky top-0 z-10 bg-bg-secondary"
            style={{ paddingLeft: `${sidebarWidth + 24}px` }}
          >
            <span className="text-base font-medium text-white">
              {selectedTags[0] || "新对话"}
            </span>
            
            <div className="flex items-center gap-4">
               {/* Right Actions */}
            </div>
          </div>

          {/* Messages Area - 对话内容，在整个屏幕中居中 */}
          <div className="flex-1 overflow-y-auto py-6 custom-scrollbar">
            <div className="max-w-180 mx-auto space-y-8 px-6">
              {messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  content={message.content}
                  isUser={message.isUser}
                  suggestions={message.suggestions}
                  onSuggestionClick={(suggestion) => handleSendMessage(suggestion)}
                />
              ))}
              
              {/* Loading State */}
              {isLoading && (
                <div className="flex items-center gap-3 text-white/60 animate-pulse pl-0">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 bg-primary-purple rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-primary-pink rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-primary-orange rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm">思考中...</span>
                </div>
              )}
            </div>
          </div>

          {/* Input Area - 底部输入框，在整个屏幕中居中 */}
          <div className="p-6 flex justify-center bg-transparent relative z-10">
            <ChatInput
              onSend={handleSendMessage}
              isSending={isLoading}
              tags={selectedTags.map((tag) => ({
                label: tag,
                onRemove: () => setSelectedTags((prev) => prev.filter((t) => t !== tag)),
              }))}
            />
          </div>
        </div>
      </div>
    );
  }

  // 首页状态（带或不带侧边栏）
  return (
    <div className="h-[calc(100vh-60px)] relative bg-bg-secondary">
      {/* 侧边栏 - 固定定位，仅当 showSidebar 为 true 时显示 */}
      {showSidebar && (
        <div className="fixed left-0 top-15 h-[calc(100vh-60px)] z-20">
          <ChatSidebar
            histories={displayHistories}
            onNewChat={handleNewChat}
            onSelectChat={handleSelectChat}
            onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            activeId={undefined}
            isCollapsed={isSidebarCollapsed}
          />
        </div>
      )}

      {/* 首页内容 - 在整个屏幕居中 */}
      <div className="w-full min-h-full flex flex-col items-center px-6 pt-16 pb-12 overflow-y-auto">
      {/* Title - 32px Medium */}
      <h1 className="text-[32px] font-medium mb-12 text-center leading-tight tracking-tight">
        开启您的<span className="text-gradient font-semibold">Flowx AI</span>，百变风格任你选择
      </h1>

      {/* Chat Input - 720px宽度 */}
      <div className="w-full max-w-180 relative z-10">
        <ChatInput
          onSend={handleSendMessage}
          tags={selectedTags.map((tag) => ({
            label: tag,
            onRemove: () => setSelectedTags((prev) => prev.filter((t) => t !== tag)),
          }))}
          isSending={isLoading}
        />
        
        {/* Agent Cards - 悬浮在输入框下方 */}
        <div className="flex gap-4 mt-6 justify-center">
          {displayAgents.map((agent) => (
            <AgentCard
              key={agent.name}
              icon={agent.icon}
              name={agent.name}
              description={agent.description}
              iconColor={(agent as { iconColor?: string }).iconColor}
              onClick={() => handleAgentClick(agent.name, (agent as { code?: string }).code)}
            />
          ))}
        </div>
      </div>

      {/* Tabs Section */}
      <div className="w-full max-w-[1200px] mt-16">
        {/* Tab Navigation */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1 p-1 bg-white/5 rounded-full">
            {categoryTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200
                  flex items-center gap-1.5
                  ${activeTab === tab.id
                    ? "bg-linear-to-r from-primary-purple to-primary-pink text-white shadow-lg shadow-purple-500/20"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                  }
                `}
              >
                <span className="text-base">{tab.icon}</span>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
          
          {/* Search Box */}
          <div className="relative group">
            <input
              type="text"
              placeholder="搜索智能体..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-[240px] h-10 pl-10 pr-4 bg-white/5 border border-white/10 
                         rounded-full text-sm text-white placeholder:text-white/40 
                         outline-hidden focus:border-primary-purple/50 focus:bg-white/10 
                         transition-all duration-300"
            />
            <svg
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 group-focus-within:text-primary-purple transition-colors"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="M21 21l-4.35-4.35" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* 当前分类说明 */}
        <div className="mb-4 flex items-center gap-2">
          <span className="text-lg">
            {categoryTabs.find(t => t.id === activeTab)?.icon}
          </span>
          <span className="text-white/80 font-medium">
            {categoryTabs.find(t => t.id === activeTab)?.name}
          </span>
          <span className="text-white/40 text-sm">
            · {displayWorkflows.length} 个智能体
          </span>
        </div>

        {/* Workflow Grid - 6列网格，20px间距 */}
        <div className="grid grid-cols-6 gap-5">
          {displayWorkflows.map((workflow, index) => (
            <WorkflowCard
              key={`${activeTab}-${index}`}
              title={workflow.title}
              description={workflow.description}
              thumbnail={workflow.thumbnail}
              onClick={() => handleAgentClick(workflow.title, workflow.code)}
            />
          ))}
        </div>

        {/* 空状态 */}
        {displayWorkflows.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="text-4xl mb-4">🔍</div>
            <p className="text-white/60">没有找到匹配的智能体</p>
            <button 
              onClick={() => setSearchQuery("")}
              className="mt-4 px-4 py-2 text-sm text-primary-purple hover:text-primary-pink transition-colors"
            >
              清除搜索
            </button>
          </div>
        )}
      </div>
      </div>
    </div>
  );
}

export default XiaowanPage;
